import React, { useState } from 'react';
import { Table, CartItem } from '../../types';
import { db } from '../../db';

interface SyncQueueItem {
  type: string;
  action: string;
  data: unknown;
  status: string;
  timestamp: number;
}

export function useTables(
  addToSyncQueue: (item: SyncQueueItem) => Promise<unknown>,
  setLastNotification: React.Dispatch<React.SetStateAction<{ tableId: number; timestamp: number } | null>>,
  hasPermission: (key: string) => boolean
) {
  const [tables, setTables] = useState<Table[]>([]);

  const addTable = async (item: Table) => {
    if (!hasPermission('MANAGE_TABLES')) throw new Error('PERMISSION_DENIED:addTable');
    const itemWithToken = { ...item, qrToken: item.qrToken || crypto.randomUUID() };
    await db.restaurantTables.add(itemWithToken);
    await db.syncStack.add({ type: 'table' as any, action: 'create', data: itemWithToken, status: 'pending', timestamp: Date.now() });
    setTables(prev => [...prev, itemWithToken]);
  };

  const updateTable = async (item: Table, skipSync = false) => {
    let safeItem = { ...item };
    if (safeItem.orders && safeItem.orders.length > 0) {
      const hasUnpaid = safeItem.orders.some(i => !i.status || i.status === 'served');
      if (!hasUnpaid) {
        safeItem = {
          ...safeItem,
          status: 'empty',
          orders: [],
          total: 0,
          tempName: undefined,
          incomingOrder: undefined
        };
      }
    }

    safeItem = { ...safeItem, _updatedAt: Date.now() };
    await db.restaurantTables.put(safeItem);
    if (!skipSync) {
      await addToSyncQueue({ type: 'table' as any, action: 'update', data: safeItem, status: 'pending', timestamp: Date.now() });
    }
    setTables(prev => prev.map(i => i.id === safeItem.id ? safeItem : i));
  };

  const resetTable = async (id: number) => {
    const table = tables.find(t => t.id === id);
    if (!table) return;

    const cleanTable: Table = {
      ...table,
      status: 'empty',
      orders: [],
      incomingOrder: undefined,
      total: 0,
      time: undefined,
      tempName: undefined
    };
    await db.restaurantTables.put(cleanTable);
    await addToSyncQueue({
      type: 'table',
      action: 'update',
      data: cleanTable,
      status: 'pending',
      timestamp: Date.now()
    });
    setTables(prev => prev.map(t => t.id === id ? cleanTable : t));
  };

  const transferTable = async (sourceId: number, targetId: number) => {
    const sourceTable = tables.find(t => t.id === sourceId);
    const targetTable = tables.find(t => t.id === targetId);

    if (!sourceTable || !targetTable) return;
    if (targetTable.status !== 'empty') {
      return;
    }

    const updatedTarget: Table = {
      ...targetTable,
      status: sourceTable.status,
      orders: sourceTable.orders,
      total: sourceTable.total,
      time: sourceTable.time,
      incomingOrder: sourceTable.incomingOrder
    };

    const updatedSource: Table = {
      ...sourceTable,
      status: 'empty',
      orders: [],
      total: 0,
      time: undefined,
      incomingOrder: undefined
    };

    await db.transaction('rw', db.restaurantTables, db.syncStack, async () => {
      await db.restaurantTables.put(updatedTarget);
      await db.restaurantTables.put(updatedSource);
    });
    await addToSyncQueue({
      type: 'table',
      action: 'update',
      data: updatedTarget,
      status: 'pending',
      timestamp: Date.now()
    });
    await addToSyncQueue({
      type: 'table',
      action: 'update',
      data: updatedSource,
      status: 'pending',
      timestamp: Date.now()
    });
    setTables(prev => prev.map(t => {
      if (t.id === targetId) return updatedTarget;
      if (t.id === sourceId) return updatedSource;
      return t;
    }));
  };

  const transferTableItems = async (sourceId: number, targetId: number, itemIds: string[]) => {
    const sourceTable = tables.find(t => t.id === sourceId);
    const targetTable = tables.find(t => t.id === targetId);

    if (!sourceTable || !targetTable) return;

    const indicesToMove = itemIds.map(Number).sort((a, b) => b - a);
    const sourceOrders = [...(sourceTable.orders || [])];
    const targetOrders = [...(targetTable.orders || [])];
    const movedItems: CartItem[] = [];

    indicesToMove.forEach(index => {
      if (index >= 0 && index < sourceOrders.length) {
        movedItems.push(sourceOrders[index]);
        sourceOrders.splice(index, 1);
      }
    });

    if (movedItems.length === 0) return;

    movedItems.forEach(item => targetOrders.push(item));

    const calcTotal = (orders: CartItem[]) => orders.reduce((sum, item) => {
      let price = item.price;
      if (item.discountValue) {
        if (item.discountType === 'percent') price = item.price * (1 - item.discountValue / 100);
        else price = Math.max(0, item.price - item.discountValue);
      }
      return sum + (price * item.quantity);
    }, 0);

    const newSourceTotal = calcTotal(sourceOrders);
    const newTargetTotal = calcTotal(targetOrders);

    const updatedSource: Table = {
      ...sourceTable,
      orders: sourceOrders,
      total: newSourceTotal,
      status: sourceOrders.length === 0 ? 'empty' : 'occupied'
    };

    const updatedTarget: Table = {
      ...targetTable,
      orders: targetOrders,
      total: newTargetTotal,
      status: 'occupied',
    };

    await db.transaction('rw', db.restaurantTables, db.syncStack, async () => {
      await db.restaurantTables.put(updatedSource);
      await db.restaurantTables.put(updatedTarget);
    });
    await addToSyncQueue({
      type: 'table',
      action: 'update',
      data: updatedSource,
      status: 'pending',
      timestamp: Date.now()
    });
    await addToSyncQueue({
      type: 'table',
      action: 'update',
      data: updatedTarget,
      status: 'pending',
      timestamp: Date.now()
    });
    setTables(prev => prev.map(t => {
      if (t.id === sourceId) return updatedSource;
      if (t.id === targetId) return updatedTarget;
      return t;
      return t;
    }));
  };

  const sendQrOrder = async (tableId: number, items: CartItem[]) => {
    const table = tables.find(t => t.id === tableId);
    if (!table || !items || items.length === 0) return;

    const existingIncoming = table.incomingOrder || [];
    const nextIncomingOrder = [...existingIncoming, ...items];

    const updatedTable = {
      ...table,
      incomingOrder: nextIncomingOrder
    };

    await updateTable(updatedTable);
    setLastNotification({ tableId, timestamp: Date.now() });
  };

  const deleteTable = async (id: number) => {
    if (!hasPermission('MANAGE_TABLES')) throw new Error('PERMISSION_DENIED:deleteTable');
    await db.restaurantTables.delete(id);
    await db.syncStack.add({ type: 'table' as any, action: 'delete', data: { id }, status: 'pending', timestamp: Date.now() });
    setTables(prev => prev.filter(i => i.id !== id));
  };

  return {
    tables,
    setTables,
    addTable,
    updateTable,
    resetTable,
    transferTable,
    transferTableItems,
    sendQrOrder,
    deleteTable,
  };
}
